import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Mic, Square, Pause, Play, Loader2, FileDown, Copy, Mail, Printer, Sparkles,
  ShieldCheck, Trash2, Cloud, X, Info, CheckCircle2, AlertTriangle, FileText
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  isSpeechRecognitionSupported,
  createRecognizer,
  processFinalChunk,
  buildDocxBlob,
} from "@/lib/dictation";
import ConfidenceBadge from "@/components/ConfidenceBadge";
import { exportToPDF } from "@/lib/pdf";

const NOTE_TYPES = [
  { id: "soap", label: "SOAP Note" },
  { id: "progress", label: "Progress Note" },
  { id: "discharge", label: "Discharge Summary" },
  { id: "operative", label: "Operative Note" },
  { id: "consultation", label: "Consultation Note" },
  { id: "orders", label: "Doctor's Orders" },
  { id: "treatment", label: "Treatment Plan" },
  { id: "referral", label: "Referral Letter" },
  { id: "prescription", label: "Prescription Note" },
  { id: "custom", label: "Custom (free form)" },
];

const CLOUD_PROVIDERS = [
  { id: "google_drive", label: "Google Drive", note: "Requires admin to enable Google OAuth" },
  { id: "dropbox", label: "Dropbox", note: "Requires admin to enable Dropbox OAuth" },
  { id: "onedrive", label: "OneDrive", note: "Requires admin to enable Microsoft OAuth" },
  { id: "icloud", label: "iCloud", note: "iOS only — use native Share Sheet" },
];

const ONBOARD_KEY = "zidamd-dictation-onboarded-v1";

const PRIVACY_TEXT =
  "Your notes stay yours. ZidaMD transcribes and formats your dictation but never stores your clinical notes. Your notes exist only in this session and are permanently cleared when you export or log out.";

const SAMPLE_PROMPT =
  "Patient is a 45 year old male presenting with chest pain that started two hours ago, radiating to the left arm, associated with diaphoresis. New paragraph. Vitals stable. Blood pressure one forty over ninety, heart rate ninety two, oxygen saturation ninety eight percent on room air.";

function logAudit(payload) {
  // Best-effort metadata audit log; ignore failures to never block the user.
  api.post("/dictation/audit", payload).catch(() => {});
}

function dl(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function Dictation() {
  const [supported] = useState(() => isSpeechRecognitionSupported());
  const [showOnboard, setShowOnboard] = useState(() => !localStorage.getItem(ONBOARD_KEY));
  const [noteType, setNoteType] = useState("soap");
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [buffer, setBuffer] = useState("");
  const [interim, setInterim] = useState("");
  const [formatting, setFormatting] = useState(false);
  const [formatted, setFormatted] = useState(null);
  const [showCloudModal, setShowCloudModal] = useState(false);
  const startedAt = useRef(null);
  const recRef = useRef(null);
  const editorRef = useRef(null);

  const noteLabel = useMemo(
    () => NOTE_TYPES.find((n) => n.id === noteType)?.label || "Note",
    [noteType]
  );

  useEffect(() => () => {
    // Cleanup on unmount: stop recognizer + wipe buffer so notes never persist.
    try { recRef.current?.stop(); } catch { /* ignore */ }
    setBuffer(""); setInterim(""); setFormatted(null);
  }, []);

  // -------------------- Recording --------------------
  const startRecording = () => {
    if (!supported) {
      toast.error("Your browser doesn't support voice dictation. Use iPhone Safari or Chrome.");
      return;
    }
    try {
      const rec = createRecognizer({
        onInterim: (txt) => setInterim(txt),
        onFinal: (chunk) => {
          setBuffer((prev) => {
            const { newBuffer, addition } = processFinalChunk(prev, chunk);
            const sep = newBuffer && !/[\s\n]$/.test(newBuffer) ? " " : "";
            return (newBuffer + sep + addition).trimStart();
          });
          setInterim("");
        },
        onError: (err) => {
          if (err === "not-allowed" || err === "service-not-allowed") {
            toast.error("Microphone permission denied. Enable mic access in your browser settings.");
          } else if (err === "no-speech") {
            // silent — common on iOS when paused
          } else {
            toast.error(`Voice error: ${err}`);
          }
        },
      });
      recRef.current = rec;
      rec.start();
      startedAt.current = Date.now();
      setRecording(true); setPaused(false);
    } catch (e) {
      toast.error(e.message || "Could not start dictation");
    }
  };

  const stopRecording = () => {
    try { recRef.current?.stop(); } catch { /* ignore */ }
    setRecording(false); setPaused(false); setInterim("");
  };

  const togglePause = () => {
    if (paused) {
      try { recRef.current?.start(); setPaused(false); } catch { /* ignore */ }
    } else {
      try { recRef.current?.stop(); setPaused(true); } catch { /* ignore */ }
    }
  };

  const clearAll = () => {
    if (!window.confirm("Clear the current dictation and all AI suggestions?")) return;
    stopRecording();
    setBuffer(""); setInterim(""); setFormatted(null);
    logAudit({ note_type: noteType, char_count: 0, duration_sec: 0, export_destination: "discarded" });
    toast.success("Cleared");
  };

  // -------------------- AI Format --------------------
  const enhanceWithAria = async () => {
    const text = (buffer + (interim ? " " + interim : "")).trim();
    if (!text) { toast.error("Nothing to enhance yet — dictate or type some text first."); return; }
    setFormatting(true);
    try {
      const { data } = await api.post("/dictation/format", {
        transcript: text, note_type: noteType, request_codes: true,
      });
      setFormatted(data);
      toast.success("ARIA formatted your note");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI formatting failed");
    } finally { setFormatting(false); }
  };

  // -------------------- Exports --------------------
  const finalText = () => formatted?.formatted_note || buffer.trim();
  const durationSec = () => startedAt.current ? Math.round((Date.now() - startedAt.current) / 1000) : 0;

  const exportPDF = () => {
    const text = finalText();
    if (!text) { toast.error("Nothing to export"); return; }
    exportToPDF(`${noteLabel} — ZidaMD`, text);
    logAudit({ note_type: noteType, char_count: text.length, duration_sec: durationSec(), export_destination: "pdf" });
    toast.success("PDF opened in a new tab");
  };

  const exportDOCX = () => {
    const text = finalText();
    if (!text) { toast.error("Nothing to export"); return; }
    const blob = buildDocxBlob(`${noteLabel} — ZidaMD`, text);
    dl(blob, `${noteType}-${Date.now()}.docx`);
    logAudit({ note_type: noteType, char_count: text.length, duration_sec: durationSec(), export_destination: "docx" });
    toast.success("Word document downloaded");
  };

  const exportTXT = () => {
    const text = finalText();
    if (!text) { toast.error("Nothing to export"); return; }
    dl(new Blob([text], { type: "text/plain" }), `${noteType}-${Date.now()}.txt`);
    logAudit({ note_type: noteType, char_count: text.length, duration_sec: durationSec(), export_destination: "txt" });
    toast.success("Text file downloaded");
  };

  const copyClipboard = async () => {
    const text = finalText();
    if (!text) { toast.error("Nothing to copy"); return; }
    try {
      await navigator.clipboard.writeText(text);
      logAudit({ note_type: noteType, char_count: text.length, duration_sec: durationSec(), export_destination: "clipboard" });
      toast.success("Copied — paste into Epic / Athena / your EMR");
    } catch {
      toast.error("Clipboard blocked by browser");
    }
  };

  const sendEmail = () => {
    const text = finalText();
    if (!text) { toast.error("Nothing to send"); return; }
    const subj = encodeURIComponent(`${noteLabel} — ${new Date().toLocaleDateString()}`);
    const body = encodeURIComponent(text + "\n\n— Dictated via ZidaMD (your notes are not stored by ZidaMD)");
    window.location.href = `mailto:?subject=${subj}&body=${body}`;
    logAudit({ note_type: noteType, char_count: text.length, duration_sec: durationSec(), export_destination: "email" });
  };

  const doPrint = () => {
    const text = finalText();
    if (!text) { toast.error("Nothing to print"); return; }
    const w = window.open("", "_blank");
    if (!w) { toast.error("Pop-up blocked"); return; }
    w.document.write(`<html><head><title>${noteLabel}</title><style>
      body{font:14px -apple-system,BlinkMacSystemFont,sans-serif;padding:40px;max-width:720px;margin:auto;color:#1a1a2e}
      h1{font-size:18px;border-bottom:2px solid #4c1d95;padding-bottom:8px}
      pre{white-space:pre-wrap;font-family:inherit;line-height:1.6}
      .footer{margin-top:32px;font-size:10px;color:#6b7280;border-top:1px solid #e5e7eb;padding-top:8px}
    </style></head><body>
      <h1>${noteLabel}</h1>
      <pre>${text.replace(/</g, "&lt;")}</pre>
      <div class="footer">ZidaMD — Built to adapt. Designed to perform. — does not store clinical notes.</div>
    </body></html>`);
    w.document.close(); w.focus(); w.print();
    logAudit({ note_type: noteType, char_count: text.length, duration_sec: durationSec(), export_destination: "print" });
  };

  // -------------------- Onboarding --------------------
  const dismissOnboard = (tryIt = false) => {
    localStorage.setItem(ONBOARD_KEY, "1");
    setShowOnboard(false);
    if (tryIt) setBuffer(SAMPLE_PROMPT);
  };

  return (
    <div data-testid="dictation-page" className="max-w-6xl mx-auto pb-24">
      {/* Privacy banner — ALWAYS visible at top */}
      <div className="rounded-lg border-l-4 border-emerald-600 bg-emerald-50 p-4 mb-6 flex items-start gap-3" data-testid="dictation-privacy-banner">
        <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
        <div className="text-sm text-emerald-900 leading-relaxed">{PRIVACY_TEXT}</div>
      </div>

      <div className="mb-5">
        <div className="eyebrow mb-1">ARIA Dictation</div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-brand-text">Dictate. Transcribe. Your way.</h1>
        <p className="text-sm text-brand-muted mt-2 max-w-2xl">
          Voice-to-note for bedside, office, or anywhere. Phone, tablet, laptop. ARIA cleans up the transcript, suggests codes, and you stay in control of every word.
        </p>
      </div>

      {!supported && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 mb-6 flex items-start gap-3 text-sm text-amber-900" data-testid="dictation-unsupported">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <div>
            <div className="font-semibold">Voice dictation isn&apos;t supported in this browser.</div>
            <div className="mt-1">Use <b>iPhone Safari</b>, <b>Android Chrome</b>, or <b>desktop Chrome/Edge</b>. You can still type below and use ARIA to format.</div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        {/* LEFT — dictation + editor */}
        <div className="space-y-5">
          <div className="section-card">
            <label className="text-xs font-semibold text-brand-muted uppercase tracking-wide block mb-2">Note type</label>
            <select
              value={noteType}
              onChange={(e) => setNoteType(e.target.value)}
              className="w-full border border-brand rounded-md px-3 py-3 text-base bg-white"
              style={{ minHeight: 48, fontSize: 16 }}
              data-testid="note-type-select"
            >
              {NOTE_TYPES.map((n) => (
                <option key={n.id} value={n.id}>{n.label}</option>
              ))}
            </select>
          </div>

          {/* GIANT MIC BUTTON */}
          <div className="section-card flex flex-col items-center py-8">
            <button
              type="button"
              onClick={recording ? stopRecording : startRecording}
              className={`relative flex items-center justify-center rounded-full transition-all
                ${recording
                  ? "bg-red-600 hover:bg-red-700 shadow-[0_0_0_8px_rgba(220,38,38,0.18)] animate-pulse"
                  : "bg-brand-primary hover:opacity-90 shadow-lg"
                }`}
              style={{ width: 144, height: 144, minWidth: 144 }}
              data-testid="mic-button"
              aria-label={recording ? "Stop recording" : "Start recording"}
            >
              {recording
                ? <Square className="w-12 h-12 text-white" strokeWidth={2.5} />
                : <Mic className="w-14 h-14 text-white" strokeWidth={2} />
              }
            </button>
            <div className="mt-4 text-sm font-semibold tracking-wide" data-testid="mic-state">
              {recording
                ? (paused ? "Paused — tap mic to stop, or resume below" : "Listening… speak naturally")
                : "Tap to start dictating"}
            </div>
            {recording && (
              <button onClick={togglePause}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm bg-brand-bg hover:bg-brand-bg/80"
                style={{ minHeight: 44 }}
                data-testid="pause-toggle">
                {paused ? <><Play className="w-4 h-4" /> Resume</> : <><Pause className="w-4 h-4" /> Pause</>}
              </button>
            )}
            <div className="mt-4 text-[11px] text-brand-muted text-center max-w-xs leading-relaxed">
              Say <b>&quot;period&quot;</b>, <b>&quot;comma&quot;</b>, <b>&quot;new paragraph&quot;</b>, <b>&quot;new line&quot;</b>, <b>&quot;delete that&quot;</b>, <b>&quot;scratch that&quot;</b>
            </div>
          </div>

          {/* LIVE TRANSCRIPTION + EDITOR */}
          <div className="section-card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-brand-text">Live transcription</div>
              <div className="text-[10px] text-brand-muted uppercase tracking-wider">{(buffer + interim).length} chars</div>
            </div>
            <textarea
              ref={editorRef}
              value={buffer + (interim ? " " + interim : "")}
              onChange={(e) => { setBuffer(e.target.value); setInterim(""); }}
              placeholder="Your dictation appears here as you speak. You can also type or edit directly."
              className="w-full border border-brand rounded-md p-3 leading-relaxed font-mono"
              style={{ minHeight: 220, fontSize: 16 }}
              data-testid="transcription-editor"
            />
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                type="button" onClick={enhanceWithAria} disabled={formatting || !buffer.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-brand-primary text-white hover:opacity-90 disabled:opacity-40"
                style={{ minHeight: 44 }}
                data-testid="enhance-aria-btn">
                {formatting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {formatting ? "ARIA is formatting…" : "Enhance with ARIA"}
              </button>
              <button
                type="button" onClick={clearAll} disabled={!buffer && !formatted}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border border-brand text-brand-text hover:bg-brand-bg disabled:opacity-40"
                style={{ minHeight: 44 }}
                data-testid="clear-btn">
                <Trash2 className="w-4 h-4" /> Clear
              </button>
            </div>
          </div>

          {/* AI FORMATTED RESULT */}
          {formatted && (
            <div className="section-card" data-testid="aria-result">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-brand-primary" />
                  <div className="text-sm font-semibold text-brand-text">ARIA formatted {noteLabel.toLowerCase()}</div>
                </div>
                <ConfidenceBadge
                  compact
                  confidence="moderate"
                  sources={[
                    { name: "AMA Documentation Guidelines", year: 2024 },
                    { name: "CMS E/M coding rules", year: 2024 },
                  ]}
                  modelRef="Claude Sonnet 4.5"
                  testid="dictation-confidence"
                />
              </div>
              <textarea
                value={formatted.formatted_note}
                onChange={(e) => setFormatted({ ...formatted, formatted_note: e.target.value })}
                className="w-full border border-brand rounded-md p-3 leading-relaxed"
                style={{ minHeight: 240, fontSize: 15 }}
                data-testid="formatted-editor"
              />
              {formatted.disclaimer && (
                <div className="mt-2 text-[10px] text-brand-muted italic flex items-start gap-1">
                  <Info className="w-3 h-3 mt-0.5 shrink-0" /> {formatted.disclaimer}
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — sticky AI panel + exports */}
        <aside className="space-y-5 lg:sticky lg:top-4 self-start">
          {formatted && (
            <>
              {(formatted.icd10_suggestions?.length > 0 || formatted.cpt_suggestions?.length > 0) && (
                <div className="section-card" data-testid="code-suggestions">
                  <div className="text-sm font-semibold text-brand-text mb-3">ARIA code suggestions</div>
                  {formatted.icd10_suggestions?.length > 0 && (
                    <div className="mb-3">
                      <div className="text-[10px] uppercase tracking-wider text-brand-muted mb-1 flex items-center gap-1">
                        ICD-10
                        <span className="text-[9px] text-emerald-700 normal-case font-normal">· verified against CMS 2026</span>
                      </div>
                      <ul className="space-y-1">
                        {formatted.icd10_suggestions.map((c, i) => (
                          <li key={i} className="text-xs flex items-start gap-2 p-2 rounded bg-brand-bg" data-testid={`icd-${i}`}>
                            <code className="font-bold text-brand-primary shrink-0">{c.code}</code>
                            <span className="flex-1 text-brand-text">
                              {c.description}
                              {c.verified === false && (
                                <span className="ml-1 text-[9px] uppercase font-bold text-red-700 bg-red-50 border border-red-200 px-1 rounded">unverified</span>
                              )}
                              {c.verified && (
                                <span className="ml-1 text-[9px] uppercase font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1 rounded" title="Verified against CMS ICD-10-CM 2026">✓ CMS</span>
                              )}
                              {c.ai_description && (
                                <div className="text-[10px] text-brand-muted italic mt-0.5">
                                  AI suggested: &ldquo;{c.ai_description}&rdquo; — replaced with official CMS text.
                                </div>
                              )}
                            </span>
                            <span className="text-[9px] uppercase text-brand-muted">{c.confidence}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {formatted.cpt_suggestions?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-brand-muted mb-1">CPT</div>
                      <ul className="space-y-1">
                        {formatted.cpt_suggestions.map((c, i) => (
                          <li key={i} className="text-xs flex items-start gap-2 p-2 rounded bg-brand-bg" data-testid={`cpt-${i}`}>
                            <code className="font-bold text-brand-primary shrink-0">{c.code}</code>
                            <span className="flex-1 text-brand-text">{c.description}</span>
                            <span className="text-[9px] uppercase text-brand-muted">{c.confidence}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="mt-3 text-[10px] text-brand-muted italic">Suggestions only — verify before submission.</div>
                </div>
              )}

              {(formatted.missing_fields?.length > 0 || formatted.clarification_needed?.length > 0) && (
                <div className="section-card border-l-4 border-amber-500" data-testid="aria-flags">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <div className="text-sm font-semibold">ARIA flags</div>
                  </div>
                  {formatted.missing_fields?.map((m, i) => (
                    <div key={`m${i}`} className="text-xs text-amber-900 mb-1">• Missing: {m}</div>
                  ))}
                  {formatted.clarification_needed?.map((m, i) => (
                    <div key={`c${i}`} className="text-xs text-amber-900 mb-1">• Clarify: {m}</div>
                  ))}
                </div>
              )}
            </>
          )}

          <div className="section-card" data-testid="export-panel">
            <div className="text-sm font-semibold text-brand-text mb-3">Export & deliver</div>
            <div className="grid grid-cols-2 gap-2">
              <ExportBtn icon={FileDown} label="PDF" onClick={exportPDF} testid="export-pdf" />
              <ExportBtn icon={FileText} label="Word" onClick={exportDOCX} testid="export-docx" />
              <ExportBtn icon={FileDown} label="Text" onClick={exportTXT} testid="export-txt" />
              <ExportBtn icon={Copy} label="Clipboard" onClick={copyClipboard} testid="export-clipboard" />
              <ExportBtn icon={Mail} label="Email" onClick={sendEmail} testid="export-email" />
              <ExportBtn icon={Printer} label="Print" onClick={doPrint} testid="export-print" />
            </div>
            <button
              onClick={() => setShowCloudModal(true)}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium border border-brand text-brand-text hover:bg-brand-bg"
              style={{ minHeight: 44 }}
              data-testid="cloud-btn">
              <Cloud className="w-4 h-4" /> Cloud storage (Drive / Dropbox / OneDrive / iCloud)
            </button>
            <div className="mt-3 text-[10px] text-brand-muted italic leading-relaxed">
              After export, the note is removed from ZidaMD memory. Audit log records only metadata (note type, destination, timestamp).
            </div>
          </div>
        </aside>
      </div>

      {/* CLOUD MODAL */}
      {showCloudModal && (
        <Modal onClose={() => setShowCloudModal(false)} title="Cloud storage" testid="cloud-modal">
          <p className="text-xs text-brand-muted mb-4 leading-relaxed">
            ZidaMD never uploads your notes for you. Each provider below requires an OAuth connection to YOUR personal account. Until your admin enables these integrations, please use Download or Clipboard and upload from your device.
          </p>
          <div className="space-y-2">
            {CLOUD_PROVIDERS.map((p) => (
              <button
                key={p.id}
                onClick={() => toast.info(`${p.label} coming soon — use Download + upload manually for now`)}
                className="w-full text-left p-3 rounded-md border border-brand hover:bg-brand-bg flex items-center justify-between"
                style={{ minHeight: 56 }}
                data-testid={`cloud-${p.id}`}
              >
                <div>
                  <div className="text-sm font-semibold text-brand-text">{p.label}</div>
                  <div className="text-[10px] text-brand-muted">{p.note}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">Coming soon</span>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {/* ONBOARDING */}
      {showOnboard && (
        <Modal onClose={() => dismissOnboard(false)} title="Welcome to ARIA Dictation" testid="onboard-modal">
          <p className="text-sm text-brand-text leading-relaxed mb-3">
            Tap the big purple microphone, then dictate freely. Try saying:
          </p>
          <div className="text-xs italic bg-brand-bg p-3 rounded mb-4 leading-relaxed border-l-2 border-brand-primary">
            &quot;Patient is a 45 year old male presenting with chest pain. New paragraph. Vitals stable.&quot;
          </div>
          <div className="text-xs text-brand-muted mb-4 leading-relaxed">
            <CheckCircle2 className="w-3 h-3 inline mr-1 text-emerald-600" /> Tap mic to start • tap again to stop<br />
            <CheckCircle2 className="w-3 h-3 inline mr-1 text-emerald-600" /> Edit anytime — type or speak<br />
            <CheckCircle2 className="w-3 h-3 inline mr-1 text-emerald-600" /> ARIA suggests format + ICD-10/CPT<br />
            <CheckCircle2 className="w-3 h-3 inline mr-1 text-emerald-600" /> Export to your destination — ZidaMD never stores it
          </div>
          <div className="flex gap-2">
            <button onClick={() => dismissOnboard(true)}
              className="flex-1 px-4 py-2 rounded-md bg-brand-primary text-white text-sm font-medium"
              style={{ minHeight: 44 }}
              data-testid="onboard-try">Try the example</button>
            <button onClick={() => dismissOnboard(false)}
              className="px-4 py-2 rounded-md border border-brand text-sm"
              style={{ minHeight: 44 }}
              data-testid="onboard-skip">Skip</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const ExportBtn = ({ icon: Icon, label, onClick, testid }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium border border-brand text-brand-text hover:bg-brand-bg"
    style={{ minHeight: 48 }}
    data-testid={testid}
  >
    <Icon className="w-4 h-4" /> {label}
  </button>
);

function Modal({ children, onClose, title, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid={testid}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-brand-muted hover:text-brand-text" data-testid={`${testid}-close`}>
          <X className="w-4 h-4" />
        </button>
        {title && <div className="text-base font-semibold mb-3">{title}</div>}
        {children}
      </div>
    </div>
  );
}
